import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Modal, Spin, Button, Space, Typography, Popconfirm, Descriptions, Tabs, List, Avatar, Empty, Input, Select, Tag, Image, message, InputNumber } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, CommentOutlined, TeamOutlined, UserOutlined, SendOutlined, PlusOutlined, CloseCircleOutlined, FileExcelOutlined, UploadOutlined, FileOutlined, FileImageOutlined, PlayCircleOutlined, SoundOutlined, FilePdfOutlined, FileWordOutlined, FilePptOutlined, MailOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FilesService } from '../api/services/files.service';
import { UsersService } from '../api/services/users.service';
import { ProjectsService } from '../api/services/projects.service';
import { TasksService } from '../api/services/tasks.service';
import { BPMService } from '../api/services/bpm.service';
import { MailService } from '../api/services/mail.service';
import { useAuth } from './auth';
import LinkifyText from '../components/LinkifyText';
import { PDFDocument, rgb } from 'pdf-lib';
import jsPDF from 'jspdf';
import api from '../api/client';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FileViewerContext = createContext(null);

export const useFileViewer = () => useContext(FileViewerContext);

function formatSize(bytes) {
  if (!bytes) return '0 Б';
  const k = 1024;
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function getFileIcon(contentType, size = 16) {
  const s = { fontSize: size };
  if (!contentType) return <FileOutlined style={s} />;
  if (contentType.startsWith('image/')) return <FileImageOutlined style={{ ...s, color: '#52c41a' }} />;
  if (contentType.startsWith('video/')) return <PlayCircleOutlined style={{ ...s, color: '#1890ff' }} />;
  if (contentType.startsWith('audio/')) return <SoundOutlined style={{ ...s, color: '#722ed1' }} />;
  if (contentType.includes('pdf')) return <FilePdfOutlined style={{ ...s, color: '#ff4d4f' }} />;
  if (contentType.includes('word') || contentType.includes('document')) return <FileWordOutlined style={{ ...s, color: '#2f54eb' }} />;
  if (contentType.includes('sheet') || contentType.includes('excel')) return <FileExcelOutlined style={{ ...s, color: '#52c41a' }} />;
  if (contentType.includes('presentation') || contentType.includes('powerpoint')) return <FilePptOutlined style={{ ...s, color: '#fa8c16' }} />;
  return <FileOutlined style={s} />;
}

function getFolderTag(folderType) {
  const map = {
    personal: { color: 'blue', label: 'Личная' },
    shared: { color: 'green', label: 'Общая' },
    tops: { color: 'red', label: 'ТОПЫ' },
    department: { color: 'orange', label: 'Направление' },
  };
  const t = map[folderType];
  return t ? <Tag color={t.color}>{t.label}</Tag> : null;
}

function getFileTypeLabel(ct) {
  if (!ct) return 'Файл';
  if (ct.startsWith('image/')) return 'Изображение';
  if (ct.startsWith('video/')) return 'Видео';
  if (ct.startsWith('audio/')) return 'Аудио';
  if (ct.includes('pdf')) return 'PDF документ';
  if (ct.includes('word') || ct.includes('document')) return 'Word документ';
  if (ct.includes('sheet') || ct.includes('excel')) return 'Excel таблица';
  if (ct.includes('presentation') || ct.includes('powerpoint')) return 'Презентация';
  if (ct.startsWith('text/')) return 'Текстовый файл';
  return 'Файл';
}

import html2canvas from 'html2canvas';

export function FileViewerProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = (user?.role_level >= 5 || user?.username === 'khaminov');

  const [cardVisible, setCardVisible] = useState(false);
  const [cardFile, setCardFile] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [grantUserId, setGrantUserId] = useState(null);
  const [grantLoading, setGrantLoading] = useState(false);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const [excelEditorVisible, setExcelEditorVisible] = useState(false);
  const [excelSheets, setExcelSheets] = useState([]);
  const [excelActiveSheet, setExcelActiveSheet] = useState(0);
  const [excelEditFile, setExcelEditFile] = useState(null);
  const [excelSaving, setExcelSaving] = useState(false);

  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [sendMailModalVisible, setSendMailModalVisible] = useState(false);
  const [sendMailEmail, setSendMailEmail] = useState('');
  const [sendMailSubject, setSendMailSubject] = useState('');
  const [sendMailBody, setSendMailBody] = useState('');
  const [sendMailSending, setSendMailSending] = useState(false);

  const openFileCard = useCallback(async (fileId, metadata = null) => {
    setCardLoading(true);
    setCardVisible(true);
    try {
      const [res, usersRes, projRes, taskRes] = await Promise.all([
        FilesService.getFileCard(fileId),
        UsersService.getUsers(),
        ProjectsService.getProjects(),
        TasksService.getTasks()
      ]);
      setCardFile(res.data.file);
      setCardData(res.data);
      setAllUsers(usersRes.data);
      setProjects(projRes.data);
      setTasks(taskRes.data?.items || taskRes.data || []);
    } catch (e) {
      message.error("Ошибка загрузки карточки файла");
      setCardVisible(false);
    } finally {
      setCardLoading(false);
    }
  }, []);

  const handleSendByEmail = async () => {
    if (!sendMailEmail) return message.warning('Введите email');
    setSendMailSending(true);
    try {
      const formData = new FormData();
      formData.append('recipients', sendMailEmail);
      formData.append('subject', sendMailSubject);
      formData.append('body_text', sendMailBody);
      formData.append('existing_file_ids', cardFile.id);
      
      await MailService.sendMail(formData);
      message.success('Файл отправлен');
      setSendMailModalVisible(false);
      setSendMailEmail('');
      setSendMailSubject('');
      setSendMailBody('');
    } catch (e) {
      message.error('Ошибка отправки: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSendMailSending(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setCommentSending(true);
    try {
      await FilesService.addComment(cardFile.id, { text: commentText });
      setCommentText('');
      const res = await FilesService.getFileCard(cardFile.id);
      setCardData(res.data);
      message.success('Комментарий добавлен');
    } catch (e) {
      message.error('Ошибка добавления комментария');
    } finally {
      setCommentSending(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await FilesService.deleteComment(cardFile.id, commentId);
      const res = await FilesService.getFileCard(cardFile.id);
      setCardData(res.data);
      message.success('Комментарий удалён');
    } catch (e) {
      message.error('Ошибка удаления комментария');
    }
  };

  const handleGrantAccess = async () => {
    if (!grantUserId) return;
    setGrantLoading(true);
    try {
      await FilesService.grantAccess(cardFile.id, { user_id: grantUserId });
      setGrantUserId(null);
      const res = await FilesService.getFileCard(cardFile.id);
      setCardData(res.data);
      message.success('Доступ предоставлен');
    } catch (e) {
      message.error('Ошибка предоставления доступа');
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeAccess = async (userId) => {
    try {
      await FilesService.revokeAccess(cardFile.id, userId);
      const res = await FilesService.getFileCard(cardFile.id);
      setCardData(res.data);
      message.success('Доступ отозван');
    } catch (e) {
      message.error('Ошибка отзыва доступа');
    }
  };

  const handleExportPdf = async (file) => {
    const hide = message.loading('Экспорт в PDF... Это может занять несколько секунд', 0);
    try {
      // 1. Check if there's a BPM process for this file
      let process = null;
      let steps = [];
      if (file.task_id) {
        try {
          const procsRes = await BPMService.getTaskProcesses(file.task_id);
          process = procsRes.data.find(p => p.target_file_id === file.id && p.status !== 'cancelled');
          if (process) {
            const stepsRes = await BPMService.getInstanceSteps(process.id);
            steps = stepsRes.data;
          }
        } catch (e) {
          console.error("No process found or error fetching", e);
        }
      }

      // 2. Download the document converted to PDF
      let documentPdfBytes = null;
      try {
        const resp = await api.get(`/files/${file.id}/convert/pdf`, { responseType: 'arraybuffer' });
        documentPdfBytes = resp.data;
      } catch (e) {
        message.warning('Не удалось сконвертировать исходный документ в PDF. Будет скачан только лист согласования.');
      }

      // 3. Create the Approval Sheet PDF
      let sheetPdfBytes = null;
      if (process || documentPdfBytes) {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.top = '-9999px';
        container.style.left = '-9999px';
        container.style.width = '1120px';
        container.style.padding = '40px';
        container.style.backgroundColor = 'white';
        container.style.fontFamily = 'Arial, sans-serif';
        
        let htmlContent = `
          <div style="font-size: 10px; color: #888; margin-bottom: 20px;">
            ID Документа: ${file.id}
          </div>
          <h2 style="font-size: 20px; margin-bottom: 10px; color: #000;">
            Лист согласования к документу: ${file.original_name}
          </h2>
        `;
        
        if (process) {
          htmlContent += `
            <div style="font-size: 14px; margin-bottom: 30px; color: #000;">
              Связанная задача: ${process.title || 'Задача'} (ID: ${process.task_id})
            </div>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px; color: #000;">
              <thead>
                <tr style="border-bottom: 2px solid #000; text-align: left;">
                  <th style="padding: 8px 0;">ФИО согласующего</th>
                  <th style="padding: 8px 0;">Статус</th>
                  <th style="padding: 8px 0;">Дата</th>
                  <th style="padding: 8px 0;">ЭЦП (Хэш подписи)</th>
                </tr>
              </thead>
              <tbody>
          `;
          
          steps.forEach(step => {
            const approver = allUsers.find(u => u.id === step.approver_id);
            const name = approver ? approver.full_name : `ID: ${step.approver_id}`;
            let statusText = step.status === 'pending' ? 'Ожидает' : 
                             step.status === 'approved' ? 'Согласовано' : 'Отклонено';
            const dateStr = step.decided_at ? dayjs(step.decided_at).format('DD.MM.YYYY HH:mm') : '';
            const signStr = step.signature_hash ? step.signature_hash : '___________________ (Подпись)';
            const commentHtml = step.comment ? `<div style="font-size: 11px; color: #555; margin-top: 4px; white-space: pre-wrap; word-break: break-word;">Комментарий: ${step.comment}</div>` : '';
            
            htmlContent += `
                <tr style="border-bottom: 1px solid #ccc;">
                  <td style="padding: 10px 0; width: 30%;">
                    <div>${name}</div>
                    ${commentHtml}
                  </td>
                  <td style="padding: 10px 0; width: 10%;">${statusText}</td>
                  <td style="padding: 10px 0; width: 15%;">${dateStr}</td>
                  <td style="padding: 10px 0; word-break: break-all; width: 45%;">${signStr}</td>
                </tr>
            `;
          });
          
          htmlContent += `
              </tbody>
            </table>
          `;
        } else {
           htmlContent += `
             <div style="font-size: 14px; margin-bottom: 30px; color: #000;">
               Маршрут согласования не запущен.
             </div>
           `;
        }
        
        container.innerHTML = htmlContent;
        document.body.appendChild(container);
        
        const canvas = await html2canvas(container, { scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        document.body.removeChild(container);
        
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        
        doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        sheetPdfBytes = doc.output('arraybuffer');
      }

      // 4. Merge PDFs using pdf-lib
      let finalPdfBytes = null;
      if (documentPdfBytes && sheetPdfBytes) {
        const mergedPdf = await PDFDocument.create();
        
        try {
            const mainDoc = await PDFDocument.load(documentPdfBytes);
            
            // Add File ID to every page of the main document
            const pages = mainDoc.getPages();
            pages.forEach((page) => {
              const { height } = page.getSize();
              page.drawText(`File ID: ${file.id}`, {
                x: 10,
                y: height - 15,
                size: 8,
                color: rgb(0.5, 0.5, 0.5),
              });
            });

            const mainPages = await mergedPdf.copyPages(mainDoc, mainDoc.getPageIndices());
            mainPages.forEach((page) => mergedPdf.addPage(page));
        } catch (e) {
            console.error("Error loading main doc PDF", e);
            message.warning("Ошибка при загрузке основного документа PDF.");
        }
        
        const sheetDoc = await PDFDocument.load(sheetPdfBytes);
        const sheetPages = await mergedPdf.copyPages(sheetDoc, sheetDoc.getPageIndices());
        sheetPages.forEach((page) => mergedPdf.addPage(page));
        
        finalPdfBytes = await mergedPdf.save();
      } else if (documentPdfBytes) {
        try {
            const mainDoc = await PDFDocument.load(documentPdfBytes);
            const pages = mainDoc.getPages();
            pages.forEach((page) => {
              const { height } = page.getSize();
              page.drawText(`File ID: ${file.id}`, {
                x: 10,
                y: height - 15,
                size: 8,
                color: rgb(0.5, 0.5, 0.5),
              });
            });
            finalPdfBytes = await mainDoc.save();
        } catch (e) {
            finalPdfBytes = documentPdfBytes;
        }
      } else if (sheetPdfBytes) {
        finalPdfBytes = sheetPdfBytes;
      }

      if (finalPdfBytes) {
        const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Экспорт_${file.original_name}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
         message.error("Не удалось сформировать PDF");
      }
      
    } catch (e) {
      console.error(e);
      message.error('Произошла ошибка при экспорте');
    } finally {
      hide();
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await FilesService.deleteFile(fileId);
      message.success('Файл удалён');
      setCardVisible(false);
      window.dispatchEvent(new CustomEvent('file-deleted', { detail: { fileId } }));
    } catch (err) {
      message.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleViewFile = async (file) => {
    setCardVisible(false);
    if (file.archived === true) {
      Modal.info({
        title: 'Файл недоступен',
        content: (
          <div>
            <p>Это вложение из старой системы (до перехода на Nextcloud).</p>
            <p style={{ color: '#8c8c8c' }}>Содержимое не перенесено — файл считается архивным.</p>
          </div>
        ),
      });
      return;
    }
    const ct = file.content_type || '';
    const contentUrl = `/api/v1/files/${file.id}/content`;
    const ext = file.original_name ? file.original_name.split('.').pop().toLowerCase() : '';
    const officeExts = ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'csv', 'ppt', 'pptx', 'odp'];

    if (ct.startsWith('image/')) {
      setPreviewFile({ ...file, _contentUrl: contentUrl });
      setPreviewVisible(true);
      return;
    }
    if (ct.startsWith('audio/') || ct.startsWith('video/')) {
      setPreviewFile({ ...file, _contentUrl: contentUrl });
      setPreviewVisible(true);
      return;
    }

    if (officeExts.includes(ext)) {
      try {
        const res = await api.get(`/files/${file.id}/collabora?mode=view`);
        setPreviewFile({ ...file, _viewMode: 'collabora', _collaboraUrl: res.data.url });
        setPreviewVisible(true);
        return;
      } catch (err) {
        console.error('Collabora load failed', err);
      }
    }

    if (ct.includes('pdf')) {
      setPreviewFile({ ...file, _viewMode: 'iframe', _contentUrl: contentUrl });
      setPreviewVisible(true);
      return;
    }

    const name = (file.original_name || '').toLowerCase();
    const isTextExt = name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.csv') || name.endsWith('.log') || name.endsWith('.json');

    if (ct.startsWith('text/') || ct.includes('json') || ct.includes('xml') || ct.includes('javascript') || ct.includes('csv') || isTextExt) {
      try {
        const res = await FilesService.getFileContentText(file.id);
        setPreviewFile({ ...file, _viewMode: 'text', _textContent: res.data });
        setPreviewVisible(true);
      } catch {
        message.error('Ошибка загрузки файла');
      }
      return;
    }

    setPreviewFile({ ...file, _viewMode: 'iframe', _contentUrl: contentUrl });
    setPreviewVisible(true);
  };

  const handleEditFile = async (file) => {
    setCardVisible(false);
    const ct = file.content_type || '';
    const ext = file.original_name ? file.original_name.split('.').pop().toLowerCase() : '';
    const officeExts = ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'csv', 'ppt', 'pptx', 'odp'];

    if (officeExts.includes(ext)) {
      try {
        const res = await api.get(`/files/${file.id}/collabora?mode=edit`);
        setPreviewFile({ ...file, _viewMode: 'collabora', _collaboraUrl: res.data.url });
        setPreviewVisible(true);
        return;
      } catch (err) {
        console.error('Collabora edit load failed', err);
      }
    }

    if (ct.includes('sheet') || ct.includes('excel')) {
      try {
        const res = await FilesService.getFileExcelData(file.id);
        const sheets = res.data.sheets.map(s => {
          const rows = s.rows || [];
          const maxCols = Math.max(10, ...rows.map(r => r.length));
          const padded = rows.map(r => {
            const row = [...r];
            while (row.length < maxCols) row.push({ v: '' });
            return row;
          });
          while (padded.length < 20) {
            padded.push(Array(maxCols).fill(null).map(() => ({ v: '' })));
          }
          return { ...s, rows: padded };
        });
        if (sheets.length === 0) {
          sheets.push({ name: 'Лист1', rows: Array.from({ length: 20 }, () => Array(10).fill(null).map(() => ({ v: '' }))), merges: [], colWidths: [] });
        }
        setExcelSheets(sheets);
        setExcelActiveSheet(0);
        setExcelEditFile(file);
        setExcelEditorVisible(true);
      } catch {
        message.error('Ошибка загрузки файла для редактирования');
      }
      return;
    }

    const name = (file.original_name || '').toLowerCase();
    const isTextExt = name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.csv') || name.endsWith('.log') || name.endsWith('.json');

    if (ct.startsWith('text/') || ct.includes('json') || ct.includes('xml') || ct.includes('javascript') || ct.includes('csv') || isTextExt) {
      try {
        const res = await FilesService.getFileContentText(file.id);
        setPreviewFile({ ...file, _viewMode: 'text-edit', _textContent: res.data });
        setPreviewVisible(true);
      } catch {
        message.error('Ошибка загрузки файла');
      }
      return;
    }

    message.info('Редактирование этого типа файлов пока не поддерживается');
  };

  const handleExcelCellChange = (sheetIdx, rowIdx, colIdx, value) => {
    setExcelSheets(prev => {
      const updated = [...prev];
      const sheet = { ...updated[sheetIdx], rows: updated[sheetIdx].rows.map(r => [...r]) };
      const oldCell = sheet.rows[rowIdx][colIdx];
      const cellObj = typeof oldCell === 'object' && oldCell ? { ...oldCell } : { v: '' };
      cellObj.v = value;
      sheet.rows[rowIdx][colIdx] = cellObj;
      updated[sheetIdx] = sheet;
      return updated;
    });
  };

  const handleExcelAddRow = () => {
    setExcelSheets(prev => {
      const updated = [...prev];
      const sheet = { ...updated[excelActiveSheet] };
      const cols = sheet.rows[0]?.length || 10;
      sheet.rows = [...sheet.rows, Array(cols).fill(null).map(() => ({ v: '' }))];
      updated[excelActiveSheet] = sheet;
      return updated;
    });
  };

  const handleExcelAddCol = () => {
    setExcelSheets(prev => {
      const updated = [...prev];
      const sheet = { ...updated[excelActiveSheet] };
      sheet.rows = sheet.rows.map(r => [...r, { v: '' }]);
      updated[excelActiveSheet] = sheet;
      return updated;
    });
  };

  const handleExcelSave = async () => {
    if (!excelEditFile) return;
    setExcelSaving(true);
    try {
      await FilesService.updateFileExcelData(excelEditFile.id, { sheets: excelSheets });
      message.success('Файл сохранён');
      window.dispatchEvent(new CustomEvent('file-updated', { detail: { fileId: excelEditFile.id } }));
    } catch {
      message.error('Ошибка сохранения файла');
    } finally {
      setExcelSaving(false);
    }
  };

  return (
    <FileViewerContext.Provider value={{ openFileCard }}>
      {children}

      <Modal
        title={null}
        open={cardVisible}
        onCancel={() => { setCardVisible(false); setCardFile(null); setCardData(null); }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {cardLoading ? (
          <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
        ) : cardData ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 40 }}>{getFileIcon(cardData.file.content_type, 40)}</div>
              <div style={{ flex: 1 }}>
                <Title level={4} style={{ margin: 0 }}>{cardData.file.original_name}</Title>
                <Text type="secondary">{getFileTypeLabel(cardData.file.content_type)} — {formatSize(cardData.file.size)}</Text>
              </div>
            </div>

            <Space style={{ marginBottom: 20, flexWrap: 'wrap' }}>
              <Button type="primary" icon={<EyeOutlined />} onClick={() => handleViewFile(cardFile)}>Просмотр</Button>
              <Button type="default" icon={<MailOutlined />} onClick={() => {
                setSendMailSubject(cardFile?.original_name || '');
                setSendMailModalVisible(true);
              }}>Отправить по email</Button>
              <Button type="default" icon={<FilePdfOutlined />} onClick={() => handleExportPdf(cardFile)}>Экспорт в PDF (с листом согласования)</Button>
              {cardFile?.view_url?.endsWith('/office') && (
                <Button danger icon={<EditOutlined />} onClick={() => handleEditFile(cardFile)}>Редактировать</Button>
              )}
              {isAdmin && (
                <Popconfirm title="Удалить файл?" onConfirm={() => handleDeleteFile(cardFile.id)} okText="Удалить" cancelText="Отмена">
                  <Button danger icon={<DeleteOutlined />}>Удалить</Button>
                </Popconfirm>
              )}
            </Space>

            <Descriptions size="small" column={2} bordered style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Название">{cardData.file.title || cardData.file.original_name}</Descriptions.Item>
              <Descriptions.Item label="Тип файла">{cardData.file.file_type || getFileTypeLabel(cardData.file.content_type)}</Descriptions.Item>
              <Descriptions.Item label="Владелец">{cardData.owner_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Проект">{cardData.file.project_id ? <Link to={`/projects?projectId=${cardData.file.project_id}`} onClick={() => setCardVisible(false)}>{projects.find(p => p.id === cardData.file.project_id)?.name || `ID: ${cardData.file.project_id}`}</Link> : 'Нет'}</Descriptions.Item>
              <Descriptions.Item label="Задача">{cardData.file.task_id ? <Link to={`/tasks?taskId=${cardData.file.task_id}`} onClick={() => setCardVisible(false)}>{tasks.find(t => t.id === cardData.file.task_id)?.title || `ID: ${cardData.file.task_id}`}</Link> : 'Нет'}</Descriptions.Item>
              <Descriptions.Item label="Папка">{cardData.folder_name} {getFolderTag(cardData.folder_type)}</Descriptions.Item>
              <Descriptions.Item label="Дата загрузки">{dayjs(cardData.file.created_at).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Размер">{formatSize(cardData.file.size)}</Descriptions.Item>
              {cardData.file.description && (
                <Descriptions.Item label="Описание" span={2}><LinkifyText text={cardData.file.description} /></Descriptions.Item>
              )}
            </Descriptions>

            <Tabs items={[
              {
                key: 'comments',
                label: <span><CommentOutlined /> Комментарии ({cardData.comments?.length || 0})</span>,
                children: (
                  <div>
                    {cardData.comments?.length > 0 ? (
                      <List
                        dataSource={cardData.comments}
                        renderItem={(c) => (
                          <List.Item actions={c.user_id === user?.id || isAdmin ? [<Popconfirm title="Удалить?" onConfirm={() => handleDeleteComment(c.id)}><Button size="small" type="link" danger>Удалить</Button></Popconfirm>] : []}>
                            <List.Item.Meta
                              avatar={<Avatar icon={<UserOutlined />} size="small" />}
                              title={<span>{c.user_name || 'Пользователь'} <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>{dayjs(c.created_at).format('DD.MM.YYYY HH:mm')}</Text></span>}
                              description={<LinkifyText text={c.text} />}
                            />
                          </List.Item>
                        )}
                        style={{ maxHeight: 300, overflow: 'auto', marginBottom: 12 }}
                      />
                    ) : (
                      <Empty description="Нет комментариев" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '12px 0' }} />
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <TextArea placeholder="Написать комментарий..." value={commentText} onChange={(e) => setCommentText(e.target.value)} autoSize={{ minRows: 1, maxRows: 4 }} onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }} style={{ flex: 1 }} />
                      <Button type="primary" icon={<SendOutlined />} loading={commentSending} disabled={!commentText.trim()} onClick={handleAddComment} />
                    </div>
                  </div>
                ),
              },
              {
                key: 'access',
                label: <span><TeamOutlined /> Доступ ({cardData.access_users?.length || 0})</span>,
                children: (
                  <div>
                    {(cardData.file.owner_id === user?.id || isAdmin) && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <Select showSearch placeholder="Выберите сотрудника..." value={grantUserId} onChange={setGrantUserId} optionFilterProp="label" style={{ flex: 1 }} options={allUsers.filter(u => !(cardData.access_users || []).some(a => a.id === u.id)).map(u => ({ value: u.id, label: `${u.full_name} — ${u.department || 'Без отдела'}` }))} />
                        <Button type="primary" icon={<PlusOutlined />} loading={grantLoading} disabled={!grantUserId} onClick={handleGrantAccess}>Предоставить</Button>
                      </div>
                    )}
                    <List
                      dataSource={cardData.access_users || []}
                      renderItem={(u) => (
                        <List.Item actions={u.access_type === 'grant' && (cardData.file.owner_id === user?.id || isAdmin) ? [<Popconfirm key="rev" title="Отозвать доступ?" onConfirm={() => handleRevokeAccess(u.id)} okText="Да" cancelText="Нет"><Button size="small" type="link" danger icon={<CloseCircleOutlined />}>Отозвать</Button></Popconfirm>] : []}>
                          <List.Item.Meta avatar={<Avatar icon={<UserOutlined />} size="small" />} title={<span>{u.full_name} {u.access_type === 'grant' ? <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>Персональный</Tag> : <Tag color="default" style={{ marginLeft: 8, fontSize: 11 }}>Через папку</Tag>}</span>} description={u.department || '—'} />
                        </List.Item>
                      )}
                      style={{ maxHeight: 350, overflow: 'auto' }}
                      locale={{ emptyText: <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </div>
                ),
              },
            ]} />
          </div>
        ) : null}
      </Modal>


      <Modal title="Отправить по email" open={sendMailModalVisible} onCancel={() => setSendMailModalVisible(false)} onOk={handleSendByEmail} confirmLoading={sendMailSending} okText="Отправить">
        <div style={{ marginBottom: 10 }}>Отправка файла: <b>{cardFile?.original_name}</b></div>
        <Input placeholder="Email получателя" value={sendMailEmail} onChange={e => setSendMailEmail(e.target.value)} style={{ marginBottom: 10 }} />
        <Input placeholder="Тема письма" value={sendMailSubject} onChange={e => setSendMailSubject(e.target.value)} style={{ marginBottom: 10 }} />
        <TextArea placeholder="Текст сообщения" value={sendMailBody} onChange={e => setSendMailBody(e.target.value)} rows={4} />
      </Modal>

      <Modal
        title={previewFile?.original_name}
        open={previewVisible}
        onCancel={() => { setPreviewVisible(false); setPreviewFile(null); }}
        footer={null}
        width={previewFile?._viewMode === 'text' || previewFile?._viewMode === 'iframe' || previewFile?._viewMode === 'collabora' || previewFile?._viewMode === 'excel' ? '90%' : previewFile?.content_type?.startsWith('video/') ? 900 : 700}
        style={previewFile?._viewMode === 'iframe' || previewFile?._viewMode === 'collabora' ? { top: 20 } : undefined}
        bodyStyle={previewFile?._viewMode === 'iframe' || previewFile?._viewMode === 'collabora' ? { height: 'calc(100vh - 120px)', padding: 0 } : undefined}
        destroyOnClose
      >
        {previewFile?.content_type?.startsWith('image/') && previewFile._contentUrl && <Image src={previewFile._contentUrl} alt={previewFile.original_name} style={{ width: '100%' }} preview={false} />}
        {previewFile?.content_type?.startsWith('video/') && previewFile._contentUrl && <video controls autoPlay style={{ width: '100%', maxHeight: '70vh' }} src={previewFile._contentUrl} />}
        {previewFile?.content_type?.startsWith('audio/') && previewFile._contentUrl && <audio controls autoPlay style={{ width: '100%', marginTop: 20 }} src={previewFile._contentUrl} />}
        {previewFile?._viewMode === 'excel' && (
          <div style={{ maxHeight: '70vh', overflow: 'auto', padding: 16 }}>
            {previewFile._sheets.map((sheet, si) => (
              <div key={si}>
                {previewFile._sheets.length > 1 && <Title level={5} style={{ margin: '12px 0 8px' }}>{sheet.name}</Title>}
                {(sheet.rows || []).length === 0 ? <Empty description="Лист пуст" /> : (
                  <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 16 }}>
                    <tbody>{sheet.rows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => { const c = typeof cell === 'object' && cell ? cell : { v: cell }; return <td key={ci} style={{ border: '1px solid #d9d9d9', padding: '6px 10px', fontSize: c.fs || 13, background: c.bg || (ri % 2 === 0 ? '#fafafa' : '#fff'), fontWeight: c.bold ? 700 : undefined, fontStyle: c.italic ? 'italic' : undefined, color: c.fc || undefined, textAlign: c.ha || 'left', verticalAlign: c.va === 'center' ? 'middle' : c.va || undefined, minWidth: sheet.colWidths?.[ci] || undefined }}>{c.v != null ? String(c.v) : ''}</td>; })}</tr>)}</tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
        {previewFile?._viewMode === 'text' && <pre style={{ maxHeight: '70vh', overflow: 'auto', padding: 16, background: '#f5f5f5', borderRadius: 8, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{previewFile._textContent}</pre>}
        {previewFile?._viewMode === 'text-edit' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TextArea value={previewFile._textContent} onChange={(e) => setPreviewFile(prev => ({ ...prev, _textContent: e.target.value }))} autoSize={{ minRows: 15, maxRows: 30 }} style={{ fontFamily: 'monospace', fontSize: 13 }} />
            <Button type="primary" onClick={async () => {
              try {
                const blob = new Blob([previewFile._textContent], { type: previewFile.content_type || 'text/plain' });
                const formData = new FormData();
                formData.append('file', blob, previewFile.original_name);
                await FilesService.updateFileContent(previewFile.id, formData);
                message.success('Файл сохранён');
                window.dispatchEvent(new CustomEvent('file-updated', { detail: { fileId: previewFile.id } }));
              } catch { message.error('Ошибка сохранения'); }
            }}>Сохранить</Button>
          </div>
        )}
        {previewFile?._viewMode === 'iframe' && previewFile._contentUrl && <iframe src={previewFile._contentUrl} style={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 160px)', border: 'none' }} title={previewFile.original_name} />}
        {previewFile?._viewMode === 'collabora' && previewFile._collaboraUrl && <iframe src={previewFile._collaboraUrl} style={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 160px)', border: 'none' }} title={previewFile.original_name} allow="fullscreen" />}
      </Modal>

      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}><span><FileExcelOutlined style={{ color: '#52c41a', marginRight: 8 }} />{excelEditFile?.original_name}</span><Space><Button size="small" onClick={handleExcelAddRow}>+ Строка</Button><Button size="small" onClick={handleExcelAddCol}>+ Столбец</Button><Button type="primary" size="small" icon={<UploadOutlined />} loading={excelSaving} onClick={handleExcelSave}>Сохранить</Button></Space></div>}
        open={excelEditorVisible}
        onCancel={() => { setExcelEditorVisible(false); setExcelSheets([]); setExcelEditFile(null); }}
        footer={null} width="95%" style={{ top: 20 }} bodyStyle={{ height: 'calc(100vh - 130px)', padding: 0, overflow: 'hidden' }} destroyOnClose
      >
        {excelSheets.length > 1 && <Tabs activeKey={String(excelActiveSheet)} onChange={(k) => setExcelActiveSheet(Number(k))} size="small" style={{ padding: '0 16px' }} items={excelSheets.map((s, i) => ({ key: String(i), label: s.name }))} />}
        <div style={{ height: 'calc(100% - 40px)', overflow: 'auto', padding: '0 4px 16px' }}>
          {excelSheets[excelActiveSheet] && (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                <tr>
                  <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', background: '#f0f0f0', fontSize: 11, color: '#999', width: 40, textAlign: 'center' }}>#</th>
                  {(excelSheets[excelActiveSheet].rows[0] || []).map((_, i) => <th key={i} style={{ border: '1px solid #d9d9d9', padding: '4px 8px', background: '#f0f0f0', fontSize: 11, color: '#999', textAlign: 'center' }}>{String.fromCharCode(65 + i)}</th>)}
                </tr>
                {excelSheets[excelActiveSheet].rows.map((row, ri) => (
                  <tr key={ri}>
                    <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px', background: '#f0f0f0', fontSize: 11, color: '#999', textAlign: 'center' }}>{ri + 1}</td>
                    {row.map((cell, ci) => {
                      const c = typeof cell === 'object' && cell ? cell : { v: cell };
                      return <td key={ci} style={{ border: '1px solid #d9d9d9', padding: 0, minWidth: excelSheets[excelActiveSheet].colWidths?.[ci] || 100 }}><Input bordered={false} value={c.v != null ? String(c.v) : ''} onChange={(e) => handleExcelCellChange(excelActiveSheet, ri, ci, e.target.value)} style={{ borderRadius: 0, fontSize: c.fs || 13, background: c.bg || (ri % 2 === 0 ? '#fafafa' : '#fff'), fontWeight: c.bold ? 700 : undefined, fontStyle: c.italic ? 'italic' : undefined, color: c.fc || undefined, textAlign: c.ha || 'left' }} /></td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Modal>
    </FileViewerContext.Provider>
  );
}
